import jsPDF from 'jspdf';
import { format } from 'date-fns';
import { ChartCapture } from './captureCharts';

export type PDFOrientation = 'portrait' | 'landscape';
export type DeliveryMethod = 'download' | 'email';

export interface FullAnalyticsExportOptions {
  agencyId: string | null;
  agencyName?: string;
  customHospitalCost: number | null;
  startDate?: Date;
  endDate?: Date;
  isAllAgencies?: boolean;
  chartCaptures?: ChartCapture[];
  orientation?: PDFOrientation;
  deliveryMethod?: DeliveryMethod;
  recipientEmail?: string;
  recipientName?: string;
}

export interface ExportResult {
  success: boolean;
  method: DeliveryMethod;
  pdfBase64?: string;
  error?: string;
}

export async function exportFullAnalyticsToPDF({
  agencyId,
  agencyName,
  customHospitalCost,
  startDate,
  endDate,
  isAllAgencies = false,
  chartCaptures = [],
  orientation = 'portrait',
  deliveryMethod = 'download',
  recipientEmail,
  recipientName
}: FullAnalyticsExportOptions): Promise<ExportResult> {
  try {
    const doc = new jsPDF({ orientation });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    let yPos = 20;

    // Adjust max image height based on orientation
    const maxImageHeight = orientation === 'landscape' ? 120 : 100;

    const formatDateSafe = (date: Date | undefined, formatStr: string): string => {
      if (!date) return '';
      try {
        return format(date, formatStr);
      } catch {
        return '';
      }
    };

    const checkPageBreak = (neededSpace: number): boolean => {
      if (yPos + neededSpace > pageHeight - 30) {
        doc.addPage();
        yPos = 20;
        return true;
      }
      return false;
    };

    // ==================== PAGE 1: HEADER ====================
    
    // Header
    doc.setFillColor(34, 139, 34);
    doc.rect(0, 0, pageWidth, 40, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('Analytics Report', margin, 22);
    
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    const dateRangeText = startDate && endDate 
      ? `${formatDateSafe(startDate, 'MMM d, yyyy')} - ${formatDateSafe(endDate, 'MMM d, yyyy')}`
      : 'All Time';
    doc.text(dateRangeText, margin, 34);

    // Add orientation indicator
    doc.setFontSize(9);
    doc.text(`${orientation === 'landscape' ? '↔ Landscape' : '↕ Portrait'}`, pageWidth - margin - 30, 34);
    
    yPos = 50;
    doc.setTextColor(0, 0, 0);

    // Agency/Scope info
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    if (isAllAgencies) {
      doc.text('Scope: All Agencies (Aggregated)', margin, yPos);
    } else if (agencyName) {
      doc.text(`Agency: ${agencyName}`, margin, yPos);
    }
    yPos += 6;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated on ${formatDateSafe(new Date(), "MMMM d, yyyy 'at' h:mm a")}`, margin, yPos);
    yPos += 12;

    doc.setTextColor(0, 0, 0);

    // ==================== ADD CHART IMAGES ====================
    
    if (chartCaptures.length === 0) {
      // No charts captured, show message
      doc.setFontSize(12);
      doc.setTextColor(100, 100, 100);
      doc.text('No chart data available. Please ensure the analytics page has loaded completely.', margin, yPos);
      yPos += 20;
    } else {
      // Calculate optimal image dimensions to fit within page
      const maxWidth = pageWidth - 2 * margin;

      for (const capture of chartCaptures) {
        // Calculate scaled dimensions maintaining aspect ratio
        const aspectRatio = capture.width / capture.height;
        let imgWidth = maxWidth;
        let imgHeight = imgWidth / aspectRatio;
        
        // If too tall, scale down
        if (imgHeight > maxImageHeight) {
          imgHeight = maxImageHeight;
          imgWidth = imgHeight * aspectRatio;
        }

        // Center the image if it's narrower than max width
        const xOffset = margin + (maxWidth - imgWidth) / 2;

        // Check if we need a new page
        const neededSpace = imgHeight + 20; // Image height + title + padding
        checkPageBreak(neededSpace);

        // Add section title
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0, 0, 0);
        doc.text(capture.name, margin, yPos);
        yPos += 6;

        // Add a subtle line under the title
        doc.setDrawColor(200, 200, 200);
        doc.line(margin, yPos, margin + 60, yPos);
        yPos += 4;

        // Add the chart image
        try {
          doc.addImage(
            capture.dataUrl,
            'PNG',
            xOffset,
            yPos,
            imgWidth,
            imgHeight
          );
          yPos += imgHeight + 12;
        } catch (error) {
          console.error(`Error adding image for ${capture.name}:`, error);
          doc.setFontSize(10);
          doc.setTextColor(150, 150, 150);
          doc.text(`[Chart image could not be loaded]`, margin, yPos);
          yPos += 15;
        }
      }
    }

    // ==================== FOOTER ON EACH PAGE ====================
    const totalPages = doc.internal.pages.length - 1;
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      const footerY = pageHeight - 12;
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, footerY - 5, pageWidth - margin, footerY - 5);
      
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text('CheckFirst - Full Analytics Report', margin, footerY);
      doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin - 25, footerY);
    }

    // Generate filename
    const startStr = startDate ? formatDateSafe(startDate, 'yyyy-MM-dd') : 'all';
    const endStr = endDate ? formatDateSafe(endDate, 'yyyy-MM-dd') : 'time';
    const scopeStr = isAllAgencies ? 'all-agencies' : (agencyName ? agencyName.replace(/\s+/g, '-').toLowerCase() : 'agency');
    const filename = `analytics-report-${scopeStr}-${startStr}-to-${endStr}`;

    if (deliveryMethod === 'email' && recipientEmail) {
      // Convert PDF to base64 for email
      const pdfBase64 = doc.output('datauristring').split(',')[1];
      
      return {
        success: true,
        method: 'email',
        pdfBase64
      };
    } else {
      // Download the PDF
      doc.save(`${filename}.pdf`);
      return {
        success: true,
        method: 'download'
      };
    }
  } catch (error) {
    console.error('Error generating PDF:', error);
    return {
      success: false,
      method: deliveryMethod || 'download',
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}
